<template>
  <div class="contact-section">
    <!-- Header Section -->
    <div class="header">
      <div class="header-content">
        <h1 class="section-title text-white font-bold text-2xl">//Contacts</h1>
        <p class="section-subtitle text-white">Ping your contacts for an instant call</p>
      </div>
      <vx-tooltip text="/ contact.create" position="left">
        <button class="add-button" @click="openCreateContactModal">
          <img src="@/assets/images/pixel_create.svg" alt="Add Storage" />
        </button>
      </vx-tooltip>
    </div>

    <!-- Search and Actions -->
    <div class="controls">
      <div class="search-container">
        <svg class="search-icon" width="18" height="18" viewBox="0 0 24 24" fill="none"
          xmlns="http://www.w3.org/2000/svg">
          <path
            d="M11 19C15.4183 19 19 15.4183 19 11C19 6.58172 15.4183 3 11 3C6.58172 3 3 6.58172 3 11C3 15.4183 6.58172 19 11 19Z"
            stroke="#9CA3AF" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
          <path d="M21 21L16.65 16.65" stroke="#9CA3AF" stroke-width="2" stroke-linecap="round"
            stroke-linejoin="round" />
        </svg>
        <input v-model="searchQuery" type="text" placeholder="// search by name or email..." class="search-input"
          aria-label="Search contacts" @input="debouncedSearch" />
      </div>

      <div class="action-buttons flex items-center justify-start gap-2">
        <!-- View Toggle -->
        <div class="view-toggle flex items-center justify-start gap-2">
          <button class="view-option" :class="{ active: viewMode === 'grid' }" @click="viewMode = 'grid'">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M10 3H3V10H10V3Z" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                stroke-linejoin="round" />
              <path d="M21 3H14V10H21V3Z" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                stroke-linejoin="round" />
              <path d="M21 14H14V21H21V14Z" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                stroke-linejoin="round" />
              <path d="M10 14H3V21H10V14Z" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                stroke-linejoin="round" />
            </svg>
          </button>
          <button class="view-option" :class="{ active: viewMode === 'list' }" @click="viewMode = 'list'">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M8 6H21" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
              <path d="M8 12H21" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                stroke-linejoin="round" />
              <path d="M8 18H21" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                stroke-linejoin="round" />
              <path d="M3 6H3.01" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                stroke-linejoin="round" />
              <path d="M3 12H3.01" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                stroke-linejoin="round" />
              <path d="M3 18H3.01" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                stroke-linejoin="round" />
            </svg>
          </button>
        </div>

        <select v-model="selectedTag" class="tag-select" @change="filterByTag">
          <option value="">All Tags</option>
          <option v-for="tag in tags" :key="tag" :value="tag">{{ tag }}</option>
        </select>

        <button @click="openCreateTagModal" class="icon-button">
          <img src="@/assets/images/tag.svg" class="w-6 h-6 object-fit" alt="">
          <span>Tags</span>
        </button>
      </div>
    </div>


    <!-- Contacts Grid View -->
    <div v-if="viewMode === 'grid'" class="contacts-grid">
      <div v-for="(contact, index) in filteredContacts" :key="index" class="contact-card">
        <div class="card-header">
          <div class="avatar">
            {{ getInitials(contact.name) }}
          </div>
          <div class="contact-info">
            <h3 class="contact-name">{{ contact.name }}</h3>
            <p class="contact-email">{{ contact.email }}</p>
          </div>
          <vx-tooltip text="/ contact.call" position="top">
            <button class="call-button" @click="callContact(contact.email)">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M5 4H9L11 9L8.5 10.5C9.57096 12.6715 11.3285 14.429 13.5 15.5L15 13L20 15V19C20 20.1046 19.1046 21 18 21C14.0993 21 10.34 19.4469 7.39278 16.8741C4.77873 14.5981 3 11.2265 3 7.5C3 5.89543 3.89543 5 5 5V4Z"
                  stroke="#22C55E" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
              </svg>
            </button>
          </vx-tooltip>
        </div>

        <div class="card-details w-full flex justify-between">
          <div class="detail-item">
            <span class="detail-label">Last Invited</span>
            <span class="detail-value">{{ formatDate(contact.last_invited) }}</span>
          </div>
          <div class="detail-item">
            <span class="detail-label">Invites</span>
            <span class="detail-value">{{ contact.invite_count }}</span>
          </div>
        </div>

        <div class="card-footer flex items-center justify-between">
          <div class="tags-container">
            <span v-for="tag in contact.tags" :key="tag" class="tag" :style="{ backgroundColor: getTagColor(tag) }">
              {{ tag }}
            </span>
          </div>

          <div>
            <vx-tooltip text="/ contact.edit" position="top">
              <button class="icon-button" @click="openEditContactModal(contact)">
                <img src="@/assets/images/edit.svg" class="w-6 h-6 object-fit" alt="">
              </button>
            </vx-tooltip>
          </div>
        </div>
      </div>
    </div>

    <!-- Contacts List View (Fallback) -->
    <div v-else class="contacts-list">
      <div class="list-header">
        <div class="header-cell flex gap-2" @click="sortBy('name')">
          <span>Name</span>
          <span v-if="sortKey === 'name'" class="sort-indicator cursor-pointer">{{ sortOrder === 'asc' ? '↑' : '↓'
          }}</span>
        </div>
        <div class="header-cell" @click="sortBy('email')">
          <span>Email</span>
          <span v-if="sortKey === 'email'" class="sort-indicator">{{ sortOrder === 'asc' ? '↑' : '↓' }}</span>
        </div>
        <div class="header-cell">
          <span>Tags</span>
        </div>
        <div class="header-cell" @click="sortBy('last_invited')">
          <span>Last Invited</span>
          <span v-if="sortKey === 'last_invited'" class="sort-indicator">{{ sortOrder === 'asc' ? '↑' : '↓' }}</span>
        </div>
        <div class="header-cell">
          <span>Call</span>
        </div>
      </div>

      <div class="list-body">
        <div v-for="(contact, index) in filteredContacts" :key="index" class="contact-item">
          <div class="contact-cell name-cell">
            <span>{{ contact.name }}</span>
          </div>
          <div class="contact-cell email-cell">
            <span>{{ contact.email }}</span>
          </div>
          <div class="contact-cell tags-cell">
            <div class="tags-container">
              <span v-for="tag in contact.tags" :key="tag" class="tag">
                {{ tag }}
              </span>
            </div>
          </div>
          <div class="contact-cell date-cell">
            <span>{{ formatDate(contact.last_invited) }}</span>
          </div>
          <div class="contact-cell action-cell">
            <button class="call-button" @click="callContact(contact.email)">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M5 4H9L11 9L8.5 10.5C9.57096 12.6715 11.3285 14.429 13.5 15.5L15 13L20 15V19C20 20.1046 19.1046 21 18 21C14.0993 21 10.34 19.4469 7.39278 16.8741C4.77873 14.5981 3 11.2265 3 7.5C3 5.89543 3.89543 5 5 5V4Z"
                  stroke="#22C55E" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
    <CreateContactModal v-if="activeModal === 'createContactModal'" :closeModal="closeCreateContactModal" :tags="tags"
      @add-contact="handleAddContact" />
    <EditContactModal v-if="activeModal === 'editContactModal'" :closeModal="closeEditContactModal" :tags="tags"
      :contact="selectedContact" @update-contact="handleUpdateContact" />
    <CreateTagModal v-if="activeModal === 'createTagModal'" :closeModal="closeCreateTagModal" :tags="tags"
      @add-tag="handleAddTag" />
  </div>
</template>

<script>
import moment from 'moment';
import _ from 'lodash';
import CreateContactModal from './components/CreateContactModal.vue';
import EditContactModal from './components/EditContactModal.vue';
import CreateTagModal from './components/CreateTagModal.vue';

export default {
  name: 'ContactSection',
  components: {
    CreateContactModal,
    EditContactModal,
    CreateTagModal
  },
  data() {
    return {
      activeModal: null,
      selectedContact: null,
      viewMode: 'grid',
      showContactDialog: false,
      showTagDialog: false,
      showFilter: false,
      searchQuery: '',
      selectedTag: '',
      isLoading: false,
      sortKey: 'name',
      sortOrder: 'asc',
      contactList: [
        { name: 'Alice Smith', email: 'alice@decast.live', invite_count: 5, last_invited: '2025-04-25 14:30', tags: ['Friends', 'Colleagues'] },
        { name: 'Bob Johnson', email: 'bob@decast.live', invite_count: 2, last_invited: '2025-04-20 09:15', tags: ['Colleagues'] },
        { name: 'Charlie Brown', email: 'charlie@decast.live', invite_count: 8, last_invited: '2025-04-27 18:45', tags: ['Family', 'Friends'] },
        { name: 'Diana Prince', email: 'diana@decast.live', invite_count: 4, last_invited: '2025-04-22 11:00', tags: ['Colleagues'] },
        { name: 'Ethan Hunt', email: 'ethan@decast.live', invite_count: 10, last_invited: '2025-04-30 16:10', tags: ['Work', 'Team'] },
        { name: 'Fiona Gallagher', email: 'fiona@decast.live', invite_count: 6, last_invited: '2025-04-18 13:25', tags: ['Friends'] },
        { name: 'George Miller', email: 'george@decast.live', invite_count: 3, last_invited: '2025-04-21 08:50', tags: ['Work'] },
        { name: 'Hannah Lee', email: 'hannah@decast.live', invite_count: 7, last_invited: '2025-04-29 19:05', tags: ['Colleagues', 'Family'] },
        { name: 'Ian Wright', email: 'ian@decast.live', invite_count: 1, last_invited: '2025-04-19 10:40', tags: ['Friends'] },
        { name: 'Jane Doe', email: 'jane@decast.live', invite_count: 9, last_invited: '2025-04-26 17:00', tags: ['Family'] },
        { name: 'Kevin Hart', email: 'kevin@decast.live', invite_count: 2, last_invited: '2025-04-23 09:20', tags: ['Work', 'Colleagues'] },
        { name: 'Laura Palmer', email: 'laura@decast.live', invite_count: 5, last_invited: '2025-04-24 15:35', tags: ['Friends'] },
        { name: 'Mike Ross', email: 'mike@decast.live', invite_count: 11, last_invited: '2025-04-28 12:45', tags: ['Team'] },
        { name: 'Nina Dobrev', email: 'nina@decast.live', invite_count: 4, last_invited: '2025-04-22 14:15', tags: ['Colleagues'] },
        { name: 'Oscar Isaac', email: 'oscar@decast.live', invite_count: 6, last_invited: '2025-04-27 18:10', tags: ['Family', 'Work'] },
        { name: 'Penelope Cruz', email: 'penelope@decast.live', invite_count: 7, last_invited: '2025-04-30 10:50', tags: ['Friends'] },
        { name: 'Quentin Blake', email: 'quentin@decast.live', invite_count: 1, last_invited: '2025-04-20 07:15', tags: ['Team'] },
        { name: 'Rachel Green', email: 'rachel@decast.live', invite_count: 3, last_invited: '2025-04-25 14:20', tags: ['Colleagues', 'Friends'] },
        { name: 'Steve Rogers', email: 'steve@decast.live', invite_count: 5, last_invited: '2025-04-21 11:50', tags: ['Work', 'Team'] },
        { name: 'Tina Fey', email: 'tina@decast.live', invite_count: 8, last_invited: '2025-04-29 20:30', tags: ['Colleagues'] },
        { name: 'Uma Thurman', email: 'uma@decast.live', invite_count: 2, last_invited: '2025-04-18 08:30', tags: ['Friends'] },
        { name: 'Victor Hugo', email: 'victor@decast.live', invite_count: 4, last_invited: '2025-04-19 16:00', tags: ['Family'] },
        { name: 'Wendy Williams', email: 'wendy@decast.live', invite_count: 6, last_invited: '2025-04-23 13:40', tags: ['Team', 'Colleagues'] },
        { name: 'Xander Cage', email: 'xander@decast.live', invite_count: 9, last_invited: '2025-04-30 17:55', tags: ['Friends', 'Work'] },
        { name: 'Yasmin Khan', email: 'yasmin@decast.live', invite_count: 3, last_invited: '2025-04-24 10:25', tags: ['Colleagues'] },
        { name: 'Zachary Levi', email: 'zachary@decast.live', invite_count: 7, last_invited: '2025-04-26 19:10', tags: ['Team'] },
        { name: 'Amelia Pond', email: 'amelia@decast.live', invite_count: 5, last_invited: '2025-04-28 14:45', tags: ['Friends'] },
        { name: 'Brandon Stark', email: 'brandon@decast.live', invite_count: 6, last_invited: '2025-04-21 17:25', tags: ['Family'] },
        { name: 'Cynthia Nixon', email: 'cynthia@decast.live', invite_count: 2, last_invited: '2025-04-19 12:10', tags: ['Work'] },
        { name: 'Damian Lewis', email: 'damian@decast.live', invite_count: 4, last_invited: '2025-04-22 08:30', tags: ['Colleagues'] },
        { name: 'Elena Gilbert', email: 'elena@decast.live', invite_count: 3, last_invited: '2025-04-20 15:50', tags: ['Friends'] },
      ],
      tags: ['Friends', 'Colleagues', 'Family', 'Clients', 'Work', 'Team'],
      tagColors: {
        'Friends': '#1e40af',
        'Colleagues': '#4338ca',
        'Family': '#6b21a8',
        'Clients': '#9d174d',
        'Work': '#92400e',
        'Team': '#065f46'
      }
    };
  },
  computed: {
    filteredContacts() {
      let contacts = [...this.contactList];

      // Search filter
      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase();
        contacts = contacts.filter(
          (contact) =>
            contact.name.toLowerCase().includes(query) ||
            contact.email.toLowerCase().includes(query)
        );
      }

      // Tag filter
      if (this.selectedTag) {
        contacts = contacts.filter((contact) => contact.tags.includes(this.selectedTag));
      }

      // Sorting
      contacts.sort((a, b) => {
        const valueA = a[this.sortKey] || '';
        const valueB = b[this.sortKey] || '';
        if (this.sortKey === 'last_invited') {
          return this.sortOrder === 'asc'
            ? new Date(valueA) - new Date(valueB)
            : new Date(valueB) - new Date(valueA);
        }
        return this.sortOrder === 'asc'
          ? valueA.localeCompare(valueB)
          : valueB.localeCompare(valueA);
      });

      return contacts;
    },
  },
  methods: {
    debouncedSearch: _.debounce(function () {
      this.$forceUpdate();
    }, 300),
    sortBy(key) {
      if (this.sortKey === key) {
        this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc';
      } else {
        this.sortKey = key;
        this.sortOrder = 'asc';
      }
    },
    getInitials(name) {
      return name.split(' ').map(n => n[0]).join('').toUpperCase();
    },
    getTagColor(tag) {
      return this.tagColors[tag] || '#1f2937';
    },
    filterByTag() {
      this.$forceUpdate();
    },
    toggleFilter() {
      this.showFilter = !this.showFilter;
    },
    openTagDialog() {
      this.showTagDialog = true;
    },
    openContactDialog() {
      this.showContactDialog = true;
    },
    openCreateContactModal() {
      this.activeModal = 'createContactModal';
    },
    closeCreateContactModal() {
      this.activeModal = null;
    },
    openCreateTagModal() {
      this.activeModal = 'createTagModal';
    },
    closeCreateTagModal() {
      this.activeModal = null;
    },
    openEditContactModal(contact) {
      this.selectedContact = contact;
      this.activeModal = 'editContactModal';
    },
    closeEditContactModal() {
      this.activeModal = null;
      this.selectedContact = null;
    },
    handleAddContact(contact) {
      this.contactList.push({
        name: contact.name,
        email: contact.email,
        invite_count: 0,
        last_invited: moment().format('YYYY-MM-DD HH:mm'),
        tags: contact.tags,
      });
      this.$vs.notify({
        title: 'Success!',
        text: 'Contact added',
        color: 'success',
      });
      this.closeContactDialog();
    },
    handleAddTag({ name, color }) {
      this.tags.push(name);
      this.$set(this.tagColors, name, color);
      this.closeTagDialog();
    },
    handleUpdateContact({ originalEmail, updatedContact }) {
      const index = this.contactList.findIndex(c => c.email === originalEmail);
      if (index !== -1) {
        // Preserve invite_count and last_invited, update name, email, tags
        this.contactList[index] = {
          ...this.contactList[index],
          name: updatedContact.name,
          email: updatedContact.email,
          tags: updatedContact.tags,
        };
        this.$vs.notify({
          title: 'Success!',
          text: 'Contact updated',
          color: 'success',
        });
      }
      this.closeEditContactModal();
    },
    callContact(email) {
      // Implement call logic
      console.log(`Calling ${email}`);
    },
    formatDate(date) {
      return moment(date).format('MMM DD, YYYY HH:mm');
    },
  },
};
</script>

<style scoped>
* {
  font-family: 'JetBrains Mono', monospace !important;
}

.contact-section {
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
  padding: 24px;
  /* background-color: #000; */
}

/* Header Section */
.header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}

.header-content {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.add-button {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
  background-color: #d7df23;
}

.add-button:hover {
  transform: translateY(-1px);
}

/* Controls Section */
.controls {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

@media (min-width: 768px) {
  .controls {
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
  }
}

.search-container {
  position: relative;
  flex-grow: 1;
  max-width: 600px;
}

.search-icon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
}

.search-input {
  width: 100%;
  padding: 12px 16px 12px 40px;
  background-color: #111;
  border: 1px solid #222;
  color: #fff;
  font-size: 14px;
  transition: all 0.2s ease;
}

.search-input:focus {
  outline: none;
  border-color: #22C55E;
  /* Green */
  box-shadow: 0 0 0 2px rgba(34, 197, 94, 0.2);
}

.tag-select {
  background-color: #111;
  border: 1px solid #222;
  height: 40px;
  color: #fff;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.tag-select:focus {
  outline: none;
  border-color: #22C55E;
}

.icon-button {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 12px;
  background-color: #111;
  border: 1px solid #222;
  color: #fff;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.icon-button:hover {
  background-color: #1a1a1a;
}

.icon-button.primary {
  background-color: #22C55E;
  /* Green */
  color: #000;
  border: none;
}

.icon-button.primary:hover {
  background-color: #1DB954;
}

.view-option {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: transparent;
  border: 1px solid #222;
  color: #555;
  cursor: pointer;
  transition: all 0.2s ease;
}

.view-option:hover {
  color: #fff;
  border-color: #444;
}

.view-option.active {
  color: #d7df23;
  border-color: #d7df23;
  background-color: rgba(250, 204, 21, 0.1);
}

/* Contacts List */
.contacts-list {
  display: flex;
  flex-direction: column;
  border: 1px solid #222;
  overflow-y: scroll;
  overflow-x: hidden;
  height: auto;
  max-height: 55vh;
}

.list-header {
  display: grid;
  grid-template-columns: 2fr 2fr 1.5fr 1.5fr 0.5fr;
  padding: 16px;
  background-color: #111;
  border-bottom: 1px solid #222;
}

.header-cell font-semibold {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 600;
  color: #22C55E;
  /* Green */
  cursor: pointer;
  user-select: none;
}

.sort-indicator {
  color: #d7df23;
  /* Yellow */
}

.list-body {
  max-height: 60vh;
  overflow-y: auto;
}

.contact-item {
  display: grid;
  grid-template-columns: 2fr 2fr 1.5fr 1.5fr 0.5fr;
  padding: 16px;
  border-bottom: 1px solid #222;
  transition: background-color 0.2s ease;
}

.contact-item:last-child {
  border-bottom: none;
}

.contact-item:hover {
  background-color: #111;
}

.contact-cell {
  display: flex;
  align-items: center;
  font-size: 14px;
  color: #fff;
}


/* Contacts Grid */
.contacts-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 16px;
  /* border: 1px solid white; */
  height: auto;
  max-height: 57vh;
  overflow-y: scroll;
}

.contact-card {
  background-color: #111;
  border: 1px solid #222;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  transition: all 0.2s ease;
}

.contact-card:hover {
  transform: translateY(2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  border-color: #333;
  z-index: 99;
}

.card-header {
  display: flex;
  align-items: center;
  gap: 12px;
}

.avatar {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background-color: #22C55E;
  color: black;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 18px;
  flex-shrink: 0;
}

.contact-info {
  flex-grow: 1;
  min-width: 0;
}

.contact-name {
  font-size: 16px;
  font-weight: 600;
  color: #fff;
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.contact-email {
  font-size: 14px;
  color: #9CA3AF;
  margin: 4px 0 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.card-details {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-top: 8px;
}

.detail-item {
  display: flex;
  flex-direction: column;
}

.detail-label {
  font-size: 12px;
  color: #9CA3AF;
  margin-bottom: 4px;
}

.detail-value {
  font-size: 14px;
  color: #fff;
}

.card-footer {
  margin-top: 12px;
}

.tags-container {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.tag {
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
  color: white;
  background-color: #1f2937;
}

.call-button {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  background: rgba(34, 197, 94, 0.1);
  border-radius: 50%;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;
}

.call-button:hover {
  background: rgba(34, 197, 94, 0.2);
  transform: scale(1.05);
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .contacts-grid {
    grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  }
}

@media (max-width: 480px) {
  .contacts-grid {
    grid-template-columns: 1fr;
  }
}
</style>